import { useEffect, useState } from 'react';
import { fetchSiteSettings, updateSiteSettings } from '@/lib/api';
import type { SiteSettings } from '@/lib/types';
import LogoManager from '@/pages/admin/settings/components/LogoManager';
import MediaLibraryManager from '@/pages/admin/settings/components/MediaLibraryManager';
import ImageSlotsManager from '@/pages/admin/settings/components/ImageSlotsManager';
import HeroSlidesManager from '@/pages/admin/settings/components/HeroSlidesManager';

interface FormState {
  church_name: string;
  full_name: string;
  tagline: string;
  mission: string;
  vision: string;
  core_values: string;
  scripture: string;
  announcement_enabled: boolean;
  announcement_text: string;
  announcement_details: string;
  welcome_title: string;
  welcome_text: string;
  phones: string;
  emails: string;
  locations: string;
  service_times: string;
  impact_stats: string;
  facebook: string;
  twitter: string;
  instagram: string;
  youtube: string;
  whatsapp_number: string;
  whatsapp_enabled: boolean;
}

const EMPTY: FormState = {
  church_name: '',
  full_name: '',
  tagline: '',
  mission: '',
  vision: '',
  core_values: '',
  scripture: '',
  announcement_enabled: true,
  announcement_text: '',
  announcement_details: '',
  welcome_title: '',
  welcome_text: '',
  phones: '',
  emails: '',
  locations: '',
  service_times: '',
  impact_stats: '',
  facebook: '',
  twitter: '',
  instagram: '',
  youtube: '',
  whatsapp_number: '',
  whatsapp_enabled: true,
};

export default function SiteSettingsPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSiteSettings()
      .then((s: SiteSettings) => {
        setForm({
          church_name: s.church_name ?? '',
          full_name: s.full_name ?? '',
          tagline: s.tagline ?? '',
          mission: s.mission ?? '',
          vision: s.vision ?? '',
          core_values: s.core_values ?? '',
          scripture: s.scripture ?? '',
          announcement_enabled: s.announcement_enabled ?? true,
          announcement_text: s.announcement_text ?? '',
          announcement_details: s.announcement_details ?? '',
          welcome_title: s.welcome_title ?? '',
          welcome_text: s.welcome_text ?? '',
          phones: (s.phones ?? []).map((p) => `${p.label}:${p.number}`).join('\n'),
          emails: (s.emails ?? []).join('\n'),
          locations: (s.locations ?? [])
            .map((l) => `${l.label}|${l.address}|${l.country ?? ''}`)
            .join('\n'),
          service_times: (s.service_times ?? [])
            .map((t) => `${t.label}|${t.time}|${t.location ?? ''}`)
            .join('\n'),
          impact_stats: (s.impact_stats ?? [])
            .map((i) => `${i.label}:${i.value}:${i.suffix ?? ''}`)
            .join('\n'),
          facebook: s.social?.facebook ?? '',
          twitter: s.social?.twitter ?? '',
          instagram: s.social?.instagram ?? '',
          youtube: s.social?.youtube ?? '',
          whatsapp_number: s.whatsapp_number ?? '',
          whatsapp_enabled: s.whatsapp_enabled ?? true,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: keyof FormState, value: string | boolean) =>
    setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const phones = form.phones
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const idx = l.indexOf(':');
          const label = idx >= 0 ? l.slice(0, idx).trim() : l;
          const number = idx >= 0 ? l.slice(idx + 1).trim() : '';
          return { label, number };
        });

      const emails = form.emails.split('\n').map((l) => l.trim()).filter(Boolean);

      const locations = form.locations
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const parts = l.split('|').map((p) => p.trim());
          return { label: parts[0] ?? '', address: parts[1] ?? '', country: parts[2] ?? '' };
        });

      const service_times = form.service_times
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const parts = l.split('|').map((p) => p.trim());
          return { label: parts[0] ?? '', time: parts[1] ?? '', location: parts[2] ?? '' };
        });

      const impact_stats = form.impact_stats
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const parts = l.split(':');
          return {
            label: (parts[0] ?? '').trim(),
            value: Number(parts[1]) || 0,
            suffix: (parts[2] ?? '').trim(),
          };
        });

      await updateSiteSettings({
        church_name: form.church_name,
        full_name: form.full_name,
        tagline: form.tagline,
        mission: form.mission,
        vision: form.vision,
        core_values: form.core_values,
        scripture: form.scripture,
        announcement_enabled: form.announcement_enabled,
        announcement_text: form.announcement_text,
        announcement_details: form.announcement_details,
        welcome_title: form.welcome_title,
        welcome_text: form.welcome_text,
        phones,
        emails,
        locations,
        service_times,
        impact_stats,
        social: {
          facebook: form.facebook,
          twitter: form.twitter,
          instagram: form.instagram,
          youtube: form.youtube,
        },
        whatsapp_number: form.whatsapp_number,
        whatsapp_enabled: form.whatsapp_enabled,
      });
      setMessage('Settings saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
      </div>
    );
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';
  const labelClass = 'block text-xs font-label font-medium text-foreground-700 mb-1.5';

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Site Settings</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Control the church name, mission, announcement bar, contact details and social links.
        </p>
      </div>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Branding, media library and website images (each saves on its own) */}
        <LogoManager />
        <HeroSlidesManager />
        <MediaLibraryManager />
        <ImageSlotsManager />

        {/* Identity */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <SectionTitle icon="ri-flag-line" title="Church Identity" />
          <Field label="Church Name" value={form.church_name} onChange={(v) => set('church_name', v)} cls={inputClass} />
          <Field label="Full Name" value={form.full_name} onChange={(v) => set('full_name', v)} cls={inputClass} />
          <Field label="Tagline" value={form.tagline} onChange={(v) => set('tagline', v)} cls={inputClass} />
          <Area label="Mission" value={form.mission} onChange={(v) => set('mission', v)} cls={inputClass} />
          <Area label="Vision" value={form.vision} onChange={(v) => set('vision', v)} cls={inputClass} />
          <Area label="Core Values" value={form.core_values} onChange={(v) => set('core_values', v)} cls={inputClass} />
          <Field label="Scripture Reference" value={form.scripture} onChange={(v) => set('scripture', v)} cls={inputClass} />
          <Field label="Welcome Title" value={form.welcome_title} onChange={(v) => set('welcome_title', v)} cls={inputClass} />
          <Area label="Welcome Text" value={form.welcome_text} onChange={(v) => set('welcome_text', v)} cls={inputClass} rows={3} />
        </section>

        {/* Announcement */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <SectionTitle icon="ri-notification-3-line" title="Announcement Bar" />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.announcement_enabled}
              onChange={(e) => set('announcement_enabled', e.target.checked)}
              className="w-4 h-4 accent-primary-500"
            />
            <span className="text-sm text-foreground-700">Show announcement bar</span>
          </label>
          <Field label="Announcement Text" value={form.announcement_text} onChange={(v) => set('announcement_text', v)} cls={inputClass} />
          <Field label="Announcement Details" value={form.announcement_details} onChange={(v) => set('announcement_details', v)} cls={inputClass} />
        </section>

        {/* Contact */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <SectionTitle icon="ri-phone-line" title="Contact Information" />
          <Area
            label="Phone Numbers (one per line: Label:Number)"
            value={form.phones}
            onChange={(v) => set('phones', v)}
            cls={inputClass}
            rows={3}
            placeholder={'CMR-EDEA:+237653760724\nUSA:+16238009241'}
          />
          <Area
            label="Emails (one per line)"
            value={form.emails}
            onChange={(v) => set('emails', v)}
            cls={inputClass}
            rows={3}
            placeholder={'info@tgmci.org\ndonations@tgmci.org'}
          />
          <Field
            label="WhatsApp Number (used for the floating chat widget)"
            value={form.whatsapp_number}
            onChange={(v) => set('whatsapp_number', v)}
            cls={inputClass}
            placeholder={'+237653760724'}
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.whatsapp_enabled}
              onChange={(e) => set('whatsapp_enabled', e.target.checked)}
              className="w-4 h-4 accent-primary-500"
            />
            <span className="text-sm text-foreground-700">Show the WhatsApp chat button on the website</span>
          </label>
          <Area
            label="Locations (one per line: Label|Address|Country)"
            value={form.locations}
            onChange={(v) => set('locations', v)}
            cls={inputClass}
            rows={4}
            placeholder={'Head Office|Dansoman, Accra, Ghana|Ghana\nCameroon|Mbanda Sic. 865, Edea, Cameroon|Cameroon'}
          />
          <Area
            label="Service Times (one per line: Label|Time|Location)"
            value={form.service_times}
            onChange={(v) => set('service_times', v)}
            cls={inputClass}
            rows={3}
            placeholder={'Sunday Worship|10:00 AM|Edea, Cameroon & Accra, Ghana'}
          />
        </section>

        {/* Social */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <SectionTitle icon="ri-share-line" title="Social Media Links" />
          <Field label="Facebook URL" value={form.facebook} onChange={(v) => set('facebook', v)} cls={inputClass} />
          <Field label="Twitter / X URL" value={form.twitter} onChange={(v) => set('twitter', v)} cls={inputClass} />
          <Field label="Instagram URL" value={form.instagram} onChange={(v) => set('instagram', v)} cls={inputClass} />
          <Field label="YouTube URL" value={form.youtube} onChange={(v) => set('youtube', v)} cls={inputClass} />
        </section>

        {/* Impact stats */}
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <SectionTitle icon="ri-bar-chart-line" title="Impact Statistics" />
          <Area
            label="Statistics (one per line: Label:Value:Suffix)"
            value={form.impact_stats}
            onChange={(v) => set('impact_stats', v)}
            cls={inputClass}
            rows={5}
            placeholder={'Lives Transformed:500:+\nMeals Distributed:2000:+'}
          />
        </section>

        <div className="flex justify-end">
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary whitespace-nowrap">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: string; title: string }) {
  return (
    <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
      <i className={`${icon} text-primary-600`} aria-hidden="true" />
      {title}
    </h3>
  );
}

function Field({
  label,
  value,
  onChange,
  cls,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  cls: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cls}
        placeholder={placeholder}
      />
    </div>
  );
}

function Area({
  label,
  value,
  onChange,
  cls,
  rows = 3,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  cls: string;
  rows?: number;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${cls} resize-y`}
        rows={rows}
        placeholder={placeholder}
      />
    </div>
  );
}